import { useMemo, useState } from 'react'
import {
  Archive, BatteryCharging, Clock, Container, Cpu, Fan, Gauge, Globe, HardDrive, KeyRound, MemoryStick, Network, ScrollText,
  Server, ShieldCheck, Thermometer, TrendingUp,
} from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { SettingsCrumb } from '../../../admin/SettingsCrumb.js'
import { usePermissions } from '../../../admin/primitives.js'
import { Link } from '../../../router.js'
import { fmtDateHeure, fmtHeure } from '../../api.js'
import { BandeauFige, BandeauLecture, Pastille, TON_GRAVITE, fmtAge, fmtNombre, type Ton } from '../commun.js'
import {
  conseilAutorise, estPerime, fmtDebit, fmtPct, fmtVal, fmtVitesse, tonMesure, verdictAttention,
  type NiveauMesure, type SourceDoc,
} from '../reseau/reseau-model.js'
import { Age, Jauge, LienAppareil, ListeAttention, Section, Sources, Tuile, Vide, useDoc } from '../reseau/ui.js'
import {
  GROUPE_PREVISION, attentionServeur, capteurs, certificats, composantsIdrac, disques, disquesPhysiques, echeance,
  entites, estDocEvenements, estDocFragilite, estDocPrevisions, estDocServeur, etatConteneur, etatSante, indexer, mesure,
  oui, tonFragilite, tonPrevision, valeur, volumes,
  type Certificat, type Conteneur, type Disque, type DocEvenements, type DocFragilite, type DocPrevisions, type DocServeur, type Index,
  type Prevision,
} from './serveur-model.js'
import './serveur.css'

/**
 * INFRASTRUCTURE → SERVEUR (21/10/2026) — lu de Sonde par le relais de Gestion.
 *
 * Hôte (processeur, mémoire, charge), disques et RAID (iDRAC : contrôleur,
 * disques physiques et virtuels, températures, alimentations, ventilateurs,
 * journal matériel récent), conteneurs Docker, sauvegarde du serveur,
 * certificats, dérive d'horloge, garde collision Docker/VLAN, onduleur vu du
 * serveur, score de fragilité et prévisions (« disque plein dans N jours »).
 *
 * Honnêteté : un disque non monté est une donnée PÉRIMÉE, jamais « 0 % » ;
 * une santé non relevée est grise ; un conteneur arrêté exprès n'est pas une
 * panne ; chaque chiffre porte son âge et sa source.
 */

const TRENTE_JOURS_MOINS_UN = 29 * 24

export function ServeurPage() {
  const meta = useShellMeta()
  const perms = usePermissions()
  const details = perms.permissions.includes('sonde.details.read')

  // Périodes des événements, recalculées à l'heure (Sonde refuse plus de 31 jours).
  const heure = Math.floor(Date.now() / 3_600_000)
  const depuisDisques = useMemo(() => new Date((heure - TRENTE_JOURS_MOINS_UN) * 3_600_000).toISOString(), [heure])
  const depuisSel = useMemo(() => new Date((heure - 7 * 24) * 3_600_000).toISOString(), [heure])

  const srv = useDoc('server', estDocServeur, 30_000)
  const fragilite = useDoc('intel/fragility', estDocFragilite, 120_000)
  const previsions = useDoc('intel/forecasts', estDocPrevisions, 300_000)
  const evDisques = useDoc(`events?kind=SERVER_DISK_UNMOUNTED,SERVER_DISK_MOUNTED&limit=50&from=${encodeURIComponent(depuisDisques)}`, estDocEvenements, 60_000)
  const evSel = useDoc(`events?kind=IDRAC_SEL_ENTRY,IDRAC_SEL_CLEARED&limit=15&from=${encodeURIComponent(depuisSel)}`, estDocEvenements, 120_000)

  const doc = srv.data
  const idx = useMemo(() => indexer(doc?.generic ?? []), [doc])
  const lesDisques = useMemo(() => disques(idx, evDisques.data?.items ?? null, doc?.thresholds?.['thresholds.server.disk_pct'] ?? null), [idx, evDisques.data, doc])
  const lesCertificats = useMemo(() => certificats(idx, doc?.thresholds?.['thresholds.backup.cert_days'] ?? null), [idx, doc])
  const points = useMemo(() => attentionServeur({ doc, idx, disques: lesDisques, certificats: lesCertificats, previsions: previsions.data }),
    [doc, idx, lesDisques, lesCertificats, previsions.data])
  // Lecture en échec (dernière donnée gardée) ou collecteur muet : verdict et tuiles en gris.
  const fige = srv.perime || Boolean(doc?.collector && (doc.collector.stale || doc.collector.ok === false))
  const verdict = verdictAttention(points, Boolean(doc), fige, doc?.generated_at ?? null)
  const [toutVoir, setToutVoir] = useState(false)
  const sources = (codes: string[]) => (doc?.sources ?? []).filter((s) => codes.includes(s.code))
  const fr = fragilite.data

  return (
    <Shell active="infrastructure" meta={meta}>
      <main className="lc-page lc-page--large lc-stagger sv-res sv-srv">
        <SettingsCrumb fallback="/infrastructure" />
        <header className="sv-res-tete">
          <div className="sv-res-tete__titre">
            <h1 className="lc-title"><Server size={26} aria-hidden="true" /> Serveur</h1>
            <p className="lc-subtitle">Le serveur du club : hôte, disques, RAID, conteneurs, sauvegarde et certificats, vus par Sonde.</p>
          </div>
          <div className="sv-res-tete__verdict">
            <Pastille ton={verdict.ton}>{verdict.texte}</Pastille>
            {fr ? (
              <span className="sv-res-tete__score" title={fr.headline_fr}>
                Fragilité <strong>{fmtNombre(fr.score)}</strong>/100 <Pastille ton={tonFragilite(fr.level)}>{fr.level ?? '—'}</Pastille>
              </span>
            ) : null}
            <span className="sv-res-age">{doc ? `Relu à ${fmtHeure(doc.generated_at)}` : srv.charge ? 'Lecture…' : '—'}</span>
          </div>
        </header>

        {srv.lecture && srv.lecture.etat !== 'ok' ? <BandeauLecture lecture={srv.lecture} depuis={doc?.generated_at ?? null} /> : null}
        <BandeauFige collector={doc?.collector ?? null} />

        <Bandeau fige={fige} doc={doc} idx={idx} disques={lesDisques} certificats={lesCertificats} fragilite={fr} />

        {points.length > 0 ? (
          <section className="lc-panel sv-res-section" aria-labelledby="sv-srv-attention">
            <header className="lc-panel__head sv-res-section__tete">
              <h2 className="lc-section-title" id="sv-srv-attention">Ce qui demande un regard</h2>
              <span className="lc-hint">le plus grave d’abord · constaté par Sonde</span>
            </header>
            <div className="lc-panel__body">
              <ListeAttention points={points} ouvert={toutVoir} basculer={() => setToutVoir((v) => !v)} />
            </div>
          </section>
        ) : null}

        <SectionHote doc={doc} idx={idx} perime={srv.perime} sources={sources(['host', 'host_network'])} />
        <SectionDisques disques={lesDisques} idx={idx} previsions={previsions.data} perime={srv.perime || evDisques.perime} sources={sources(['host', 'idrac'])} />
        <SectionMateriel idx={idx} sel={evSel.data} perime={srv.perime} sources={sources(['idrac'])} details={details} />
        <SectionConteneurs doc={doc} idx={idx} perime={srv.perime} sources={sources(['docker'])} details={details} />
        <SectionSauvegarde idx={idx} certificats={lesCertificats} perime={srv.perime} sources={sources(['backups_server', 'npm'])} seuilCert={doc?.thresholds?.['thresholds.backup.cert_days'] ?? null} />
        <SectionReseauHote doc={doc} idx={idx} perime={srv.perime} sources={sources(['host_network', 'endpoints'])} />
        <SectionFragilite doc={fr} perime={fragilite.perime} lecture={fragilite.lecture?.etat === 'ok' ? null : fragilite.lecture?.message ?? null} />
        <SectionPrevisions doc={previsions.data} perime={previsions.perime} />

        <p className="sv-res-pied">
          <Link className="lc-btn" href="/infrastructure">Supervision du club</Link>
          <Link className="lc-btn lc-btn--ghost" href="/infrastructure/services">Services du serveur</Link>
        </p>
      </main>
    </Shell>
  )
}

/* ═══════════════ Bandeau de tuiles ═══════════════ */

function Bandeau({ fige, doc, idx, disques: ds, certificats: certs, fragilite }: { fige: boolean; doc: DocServeur | null; idx: Index; disques: Disque[]; certificats: Certificat[]; fragilite: DocFragilite | null }) {
  // Dernière donnée seulement : chaque tuile passe en gris (son chiffre reste lisible).
  const g = (t: Ton | 'direct'): Ton | 'direct' => (fige ? 'gris' : t)
  const h = doc?.host ?? null
  const hPerime = !h || estPerime(h.data_age_s)
  const lv = (k: string): Ton => (hPerime ? 'gris' : tonMesure(h?.levels[k] ?? null))
  const vols = volumes(idx)
  const physiques = disquesPhysiques(idx)
  const raidKo = vols.some((v) => (v.degrade ?? 0) >= 1 || v.sante === 2) || physiques.some((p) => (p.pannePredite ?? 0) >= 1 || p.sante === 2)
  const raidWarn = vols.some((v) => v.sante === 1) || physiques.some((p) => p.sante === 1)
  const raidConnu = vols.some((v) => v.sante !== null) || physiques.some((p) => p.sante !== null)
  const pireDisque = ds.filter((d) => !d.perime).reduce<Disque | null>((m, d) => (m === null || (d.usedPct ?? 0) > (m.usedPct ?? 0) ? d : m), null)
  const tonDisques: Ton = ds.length === 0 ? 'gris' : ds.some((d) => d.demonte || d.ton === 'err') ? 'err' : ds.some((d) => d.ton === 'warn') ? 'warn' : ds.every((d) => d.perime) ? 'gris' : 'ok'
  const seuilRedem = doc?.thresholds?.['thresholds.services.restarts_per_hour'] ?? null
  const etats = (doc?.containers ?? []).map((c) => etatConteneur(c, seuilRedem))
  const enMarche = (doc?.containers ?? []).filter((c) => c.running === true).length
  const tonCtr: Ton = !doc || etats.length === 0 ? 'gris' : etats.some((e) => e.ton === 'err') ? 'err' : etats.some((e) => e.ton === 'warn') ? 'warn' : 'ok'
  const b = 'backup:serveur'
  const dernierOk = oui(valeur(idx, b, 'serveur.backup_last_ok'))
  const integre = oui(valeur(idx, b, 'serveur.backup_integrity_ok'))
  const ageH = valeur(idx, b, 'serveur.backup_age_h')
  const tonSauv: Ton = dernierOk === null ? 'gris' : dernierOk === false || integre === false ? 'err' : (valeur(idx, b, 'serveur.backup_daily_gaps') ?? 0) > 0 ? 'warn' : 'ok'
  const prochain = certs.find((c) => c.jours !== null) ?? null
  const tonCert: Ton = certs.length === 0 ? 'gris' : certs.some((c) => c.niveau === 'critical') ? 'err' : certs.some((c) => c.niveau === 'warning') ? 'warn' : certs.some((c) => c.niveau === 'ok') ? 'ok' : 'gris'

  return (
    <div className="sv-res-tuiles" role="list">
      <Tuile ton={g(hPerime ? 'gris' : 'direct')} icone={<Cpu size={14} aria-hidden="true" />} label="Processeur" valeur={fmtPct(hPerime ? null : h?.cpu_pct, 0)}
        detail={h ? <>charge {fmtVal(h.load1, '', 2)} · {fmtVal(h.load5, '', 2)} · {fmtVal(h.load15, '', 2)}</> : 'Pas de donnée'} />
      <Tuile ton={g(lv('mem_avail_pct'))} icone={<MemoryStick size={14} aria-hidden="true" />} label="Mémoire libre" valeur={fmtPct(hPerime ? null : h?.mem_avail_pct, 0)}
        detail={h ? <>{fmtVal(h.mem_used_mb === null ? null : h.mem_used_mb / 1024, 'Go utilisés', 1)} · échange {fmtPct(h.swap_pct)}</> : 'Pas de donnée'} />
      <Tuile ton={g(lv('temp_max_c'))} icone={<Thermometer size={14} aria-hidden="true" />} label="Température" valeur={fmtVal(hPerime ? null : h?.temp_max_c ?? h?.temp_cpu_c, '°C')}
        detail={h ? <>processeur · <Age s={h.data_age_s} at={h.at} /></> : 'Pas de donnée'} />
      <Tuile ton={g(tonDisques)} icone={<HardDrive size={14} aria-hidden="true" />} label="Disques"
        valeur={ds.some((d) => d.demonte) ? 'Non monté' : pireDisque ? fmtPct(pireDisque.usedPct, 0) : '—'}
        detail={ds.length === 0 ? 'Pas de donnée' : ds.some((d) => d.demonte) ? `${ds.filter((d) => d.demonte).length} disque non monté : donnée périmée` : pireDisque ? `le plus plein : ${pireDisque.libelle}` : 'Données périmées'} />
      <Tuile ton={g(!raidConnu ? 'gris' : raidKo ? 'err' : raidWarn ? 'warn' : 'ok')} icone={<ShieldCheck size={14} aria-hidden="true" />} label="RAID"
        valeur={!raidConnu ? '—' : raidKo ? 'Dégradé' : raidWarn ? 'À surveiller' : 'Sain'}
        detail={raidConnu ? `${vols.length} volume${vols.length > 1 ? 's' : ''} · ${physiques.length} disque${physiques.length > 1 ? 's' : ''} physique${physiques.length > 1 ? 's' : ''}` : 'Pas de donnée iDRAC'} />
      <Tuile ton={g(tonCtr)} icone={<Container size={14} aria-hidden="true" />} label="Conteneurs" valeur={doc ? `${enMarche}/${doc.containers.length}` : '—'}
        detail={doc ? `en marche · ${etats.filter((e) => e.ton === 'off').length} arrêté(s) exprès` : 'Pas de donnée'} />
      <Tuile ton={g(tonSauv)} icone={<Archive size={14} aria-hidden="true" />} label="Sauvegarde"
        valeur={dernierOk === null ? '—' : dernierOk ? 'Réussie' : 'En échec'}
        detail={ageH === null ? 'Pas de donnée' : `dernière ${fmtAge(ageH * 3600)}`} />
      <Tuile ton={g(tonCert)} icone={<KeyRound size={14} aria-hidden="true" />} label="Certificats" valeur={prochain ? fmtVal(prochain.jours, 'j') : '—'}
        detail={prochain ? `prochaine expiration : ${prochain.hote}` : 'Pas de donnée'} />
      <Tuile ton={g(lv('clock_offset_s'))} icone={<Clock size={14} aria-hidden="true" />} label="Horloge" valeur={fmtVal(hPerime ? null : h?.clock_offset_s, 's', 2)}
        detail="écart avec l’heure de référence" />
      <Tuile ton={g(fragilite ? tonFragilite(fragilite.level) : 'gris')} icone={<Gauge size={14} aria-hidden="true" />} label="Fragilité"
        valeur={fragilite ? `${fmtNombre(fragilite.score)}/100` : '—'}
        detail={fragilite ? <>{fragilite.level ?? '—'} · <Age s={fragilite.age_s} at={fragilite.computed_at} prefixe="calculée" /></> : 'Pas de donnée'} />
    </div>
  )
}

/* ═══════════════ Hôte ═══════════════ */

function Fait({ dt, dd, niveau, titre }: { dt: string; dd: string; niveau?: NiveauMesure | undefined; titre?: string | undefined }) {
  // Sans verdict de Sonde (pas de seuil), la valeur garde sa couleur normale : seul un niveau colore.
  const cls = niveau === undefined || niveau === null ? undefined : `sv-res-txt--${tonMesure(niveau)}`
  return <div title={titre}><dt>{dt}</dt><dd className={cls}>{dd}</dd></div>
}

function SectionHote({ doc, idx, perime, sources }: { doc: DocServeur | null; idx: Index; perime: boolean; sources: SourceDoc[] }) {
  const h = doc?.host ?? null
  const fige = !h || estPerime(h.data_age_s)
  const l = (k: string): NiveauMesure | undefined => (fige ? undefined : h?.levels[k] ?? undefined)
  return (
    <Section id="sv-srv-hote" titre={h ? `Hôte ${h.host}` : 'Hôte'} icone={<Cpu size={15} aria-hidden="true" />} perime={perime || fige}
      droite={<><Sources sources={sources} />{h ? <Age s={h.data_age_s} at={h.at} prefixe="relevé" /> : null}</>}>
      {!h ? <p className="lc-hint">—</p> : (
        <div className="sv-res-carte">
          <dl className="sv-res-faits">
            <Fait dt="Processeur" dd={fmtPct(h.cpu_pct, 1)} />
            <Fait dt="Charge 1 / 5 / 15 min" dd={`${fmtVal(h.load1, '', 2)} · ${fmtVal(h.load5, '', 2)} · ${fmtVal(h.load15, '', 2)}`} />
            <Fait dt="Charge par cœur" dd={fmtVal(valeur(idx, `host:${h.host}`, 'serveur.load_per_core'), '', 3)} />
            <Fait dt="Mémoire disponible" dd={fmtPct(h.mem_avail_pct, 1)} niveau={l('mem_avail_pct')} />
            <Fait dt="Mémoire utilisée" dd={fmtVal(h.mem_used_mb === null ? null : h.mem_used_mb / 1024, 'Go', 1)} />
            <Fait dt="Mémoire d’échange" dd={fmtPct(h.swap_pct, 1)} niveau={l('swap_pct')} />
            <Fait dt="Attente processeur" dd={fmtPct(h.psi_cpu_some, 1)} titre="Part du temps où des tâches attendent le processeur (PSI)" />
            <Fait dt="Attente mémoire" dd={fmtPct(h.psi_mem_some, 1)} titre="Part du temps où des tâches attendent la mémoire (PSI)" />
            <Fait dt="Attente disques" dd={fmtPct(h.psi_io_some, 1)} titre="Part du temps où des tâches attendent les disques (PSI)" niveau={l('psi_io_some')} />
            <Fait dt="Température processeur" dd={fmtVal(h.temp_cpu_c, '°C')} />
            <Fait dt="Température maximale" dd={fmtVal(h.temp_max_c, '°C')} niveau={l('temp_max_c')} />
            <Fait dt="Processus" dd={fmtNombre(h.procs)} />
            <Fait dt="Pertes UDP (1 h)" dd={fmtNombre(h.udp_rcvbuf_err_1h)} titre="Paquets UDP perdus faute de place dans le tampon de réception" />
            <Fait dt="Dérive d’horloge" dd={fmtVal(h.clock_offset_s, 's', 2)} niveau={l('clock_offset_s')} />
            <Fait dt="Écart horloge matérielle" dd={fmtVal(valeur(idx, `host:${h.host}`, 'serveur.rtc_offset_s'), 's', 1)} />
          </dl>
        </div>
      )}
    </Section>
  )
}

/* ═══════════════ Disques ═══════════════ */

function SectionDisques({ disques: ds, idx, previsions, perime, sources }: { disques: Disque[]; idx: Index; previsions: DocPrevisions | null; perime: boolean; sources: SourceDoc[] }) {
  const vols = volumes(idx)
  const physiques = disquesPhysiques(idx)
  const raidResume = valeur(idx, 'raid:serveur', 'serveur.raid_degraded')
  return (
    <Section id="sv-srv-disques" titre="Disques et RAID" icone={<HardDrive size={15} aria-hidden="true" />} perime={perime} droite={<Sources sources={sources} />}>
      {ds.length === 0 ? <Vide titre="Aucun disque relevé" texte="Sonde n’a pas encore relevé le remplissage des disques du serveur." /> : (
        <div className="sv-res-grille">
          {ds.map((d) => {
            const prev = (previsions?.series ?? []).find((s) => s.key === 'server_disk' && s.entity === `fs:${d.cle}`) ?? null
            return <CarteDisque key={d.cle} d={d} prev={prev} />
          })}
        </div>
      )}
      <div className="sv-res-deux">
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete">
            <span className="sv-res-carte__titre">Volumes RAID</span>
            <Pastille ton={raidResume === null ? 'gris' : raidResume > 0 ? 'err' : 'ok'}>{raidResume === null ? '—' : raidResume > 0 ? `${raidResume} problème${raidResume > 1 ? 's' : ''}` : 'Aucun problème'}</Pastille>
          </div>
          {vols.length === 0 ? <p className="lc-hint">Aucun volume relevé par l’iDRAC.</p> : (
            <ul className="sv-res-lignes">
              {vols.map((v) => {
                const e = (v.degrade ?? 0) >= 1 ? { ton: 'err' as Ton, libelle: 'Dégradé' } : etatSante(v.sante)
                return (
                  <li key={v.id} className="sv-res-ligne">
                    <span className="sv-res-ligne__corps">
                      <span className="sv-res-ligne__titre">{v.libelle}</span>
                      {v.reconstruction !== null ? <span className="sv-res-ligne__meta">Reconstruction : {fmtPct(v.reconstruction)}</span> : null}
                    </span>
                    <span className="sv-res-ligne__valeurs"><Pastille ton={e.ton}>{e.libelle}</Pastille><Age s={v.age} /></span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete"><span className="sv-res-carte__titre">Disques physiques</span></div>
          {physiques.length === 0 ? <p className="lc-hint">Aucun disque physique relevé par l’iDRAC.</p> : (
            <ul className="sv-res-lignes">
              {physiques.map((p) => {
                const e = (p.pannePredite ?? 0) >= 1 ? { ton: 'err' as Ton, libelle: 'Panne annoncée' } : etatSante(p.sante)
                return (
                  <li key={p.id} className="sv-res-ligne">
                    <span className="sv-res-ligne__corps">
                      <span className="sv-res-ligne__titre">{p.libelle}</span>
                      <span className="sv-res-ligne__meta">SMART : {p.pannePredite === null ? '—' : p.pannePredite >= 1 ? 'panne prédite' : 'rien d’annoncé'} · endurance {fmtPct(p.endurance)}</span>
                    </span>
                    <span className="sv-res-ligne__valeurs"><Pastille ton={e.ton}>{e.libelle}</Pastille><Age s={p.age} /></span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </Section>
  )
}

function CarteDisque({ d, prev }: { d: Disque; prev: Prevision | null }) {
  const ech = prev ? echeance(prev) : null
  return (
    <article className={`sv-res-carte sv-res-carte--${d.demonte ? 'err' : d.ton}`}>
      <div className="sv-res-carte__tete">
        <div className="sv-res-carte__nom">
          <span className="sv-res-carte__titre">{d.libelle}</span>
          <span className="sv-res-carte__meta">{d.tailleGo !== null ? `${fmtVal(d.tailleGo / 1000, 'To', 1)}` : 'taille —'}</span>
        </div>
        <Pastille ton={d.demonte ? 'err' : d.ton}>{d.etat}</Pastille>
      </div>
      <div className="sv-srv-disque">
        <span className={`sv-srv-disque__pct sv-res-txt--${d.ton}`}>{d.perime ? '—' : fmtPct(d.usedPct, 1)}</span>
        <Jauge pct={d.usedPct} ton={d.ton} label={`Remplissage de ${d.libelle}`} />
      </div>
      {d.demonte ? (
        <p className="sv-srv-alerte">{d.message ?? 'Ce disque n’est plus monté sur le serveur : ses chiffres ne sont plus relevés.'}</p>
      ) : d.perime ? (
        <p className="lc-hint">Plus de mesure récente{d.dernierPct !== null ? ` (dernière : ${fmtPct(d.dernierPct, 1)}, ${fmtAge(d.age)})` : ''} : rien n’est affiché plutôt qu’un chiffre faux.</p>
      ) : (
        <p className="sv-res-resume">{fmtVal(d.libreGo, 'Go libres', 0)} · inodes {fmtPct(d.inodesPct, 1)}</p>
      )}
      <div className="sv-res-carte__pied">
        <Age s={d.age} at={d.at} prefixe="Relevé" />
        {prev ? <span className={`sv-res-chiffre sv-res-txt--${tonPrevision(prev.state)}`} title={prev.message_fr}><TrendingUp size={12} aria-hidden="true" /> {ech ?? (prev.confidence === 'insuffisante' ? 'tendance : pas assez d’historique' : 'pas de seuil en vue')}</span> : null}
      </div>
    </article>
  )
}

/* ═══════════════ Matériel (iDRAC) ═══════════════ */

function SectionMateriel({ idx, sel, perime, sources, details }: { idx: Index; sel: DocEvenements | null; perime: boolean; sources: SourceDoc[]; details: boolean }) {
  const comps = composantsIdrac(idx)
  const syntheses = comps.filter((c) => c.groupe === 'synthese')
  const detail = comps.filter((c) => c.groupe === 'composant')
  const fans = capteurs(idx, 'fan', 'serveur.idrac_fan_rpm')
  const temps = capteurs(idx, 'temp', 'serveur.idrac_temp_c')
  const psuV = capteurs(idx, 'psu', 'serveur.idrac_psu_input_v')
  const psuW = capteurs(idx, 'psu', 'serveur.idrac_psu_output_w')
  const conso = mesure(idx, 'idrac:system', 'serveur.idrac_power_w')
  const selN = valeur(idx, 'idrac:sel', 'serveur.idrac_sel_entries')
  const selCrit = valeur(idx, 'idrac:sel', 'serveur.idrac_sel_critical')
  const derive = valeur(idx, 'idrac:manager', 'serveur.idrac_clock_offset_s')
  const age = mesure(idx, 'idrac:system', 'serveur.idrac_health')?.data_age_s ?? null
  const ups = entites(idx, 'ups:')
  if (comps.length === 0 && fans.length === 0 && ups.length === 0) {
    return (
      <Section id="sv-srv-materiel" titre="Matériel (iDRAC)" icone={<ShieldCheck size={15} aria-hidden="true" />} perime={perime} droite={<Sources sources={sources} />}>
        <Vide titre="Aucune donnée de l’iDRAC" texte="La santé matérielle du serveur (contrôleur, disques, alimentations, ventilateurs) apparaît ici dès que Sonde lit l’iDRAC." />
      </Section>
    )
  }
  return (
    <Section id="sv-srv-materiel" titre="Matériel (iDRAC)" icone={<ShieldCheck size={15} aria-hidden="true" />} perime={perime}
      droite={<><Sources sources={sources} /><Age s={age} prefixe="relevé" /></>}>
      {syntheses.length > 0 ? (
        <ul className="sv-srv-puces" aria-label="Santé par famille">
          {syntheses.map((c) => {
            const e = etatSante(c.sante)
            return <li key={c.cle}><span>{c.libelle}</span><Pastille ton={e.ton}>{e.libelle}</Pastille></li>
          })}
        </ul>
      ) : null}
      <div className="sv-res-grille">
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete"><span className="sv-res-carte__titre"><Thermometer size={14} aria-hidden="true" /> Températures et alimentations</span></div>
          <dl className="sv-res-faits">
            {temps.map((t) => <Fait key={`t${t.n}`} dt={`Sonde ${t.n}`} dd={fmtVal(t.valeur, '°C')} />)}
            {psuV.map((p) => <Fait key={`v${p.n}`} dt={`Alimentation ${p.n}`} dd={`${fmtVal(p.valeur, 'V')}${psuW.find((w) => w.n === p.n)?.valeur != null ? ` · ${fmtVal(psuW.find((w) => w.n === p.n)?.valeur, 'W')}` : ''}`} />)}
            <Fait dt="Consommation" dd={fmtVal(conso?.value ?? null, 'W')} />
          </dl>
        </div>
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete"><span className="sv-res-carte__titre"><Fan size={14} aria-hidden="true" /> Ventilateurs</span></div>
          {fans.length === 0 ? <p className="lc-hint">—</p> : (
            <dl className="sv-res-faits">
              {fans.map((f) => <Fait key={f.n} dt={`Ventilateur ${f.n}`} dd={fmtVal(f.valeur, 'tr/min')} />)}
            </dl>
          )}
        </div>
        {detail.length > 0 ? (
          <div className="sv-res-carte">
            <div className="sv-res-carte__tete"><span className="sv-res-carte__titre">Composants</span></div>
            <ul className="sv-srv-puces sv-srv-puces--serre">
              {detail.map((c) => {
                const e = etatSante(c.sante)
                return <li key={c.cle}><span>{c.libelle}</span><Pastille ton={e.ton}>{e.libelle}</Pastille></li>
              })}
            </ul>
          </div>
        ) : null}
        {ups.length > 0 ? (
          <div className="sv-res-carte">
            <div className="sv-res-carte__tete">
              <span className="sv-res-carte__titre"><BatteryCharging size={14} aria-hidden="true" /> Onduleur vu du serveur</span>
              <Link className="sv-res-lien sv-res-chiffre" href="/technique/batiment">Bâtiment</Link>
            </div>
            <dl className="sv-res-faits">
              {ups.flatMap((e) => [...(idx.get(e)?.values() ?? [])].map((m) => (
                <Fait key={`${e}-${m.metric}`} dt={m.label} dd={fmtVal(m.value, m.unit ?? '', 1)} titre={`${e.slice(4)} · relevé ${fmtAge(m.data_age_s)}`} />
              )))}
            </dl>
          </div>
        ) : null}
      </div>

      <div className="sv-res-bloc">
        <h3 className="sv-res-sous-titre"><ScrollText size={15} aria-hidden="true" /> Journal matériel (SEL)
          <span className="lc-hint">{fmtNombre(selN)} entrée{(selN ?? 0) > 1 ? 's' : ''}, dont {fmtNombre(selCrit)} critique{(selCrit ?? 0) > 1 ? 's' : ''}{derive !== null ? ` · horloge de l’iDRAC décalée de ${fmtDuree(derive)} (Sonde corrige les dates)` : ''}</span>
        </h3>
        {!sel ? <p className="lc-hint">—</p> : sel.items.length === 0 ? (
          <p className="lc-hint">Aucune nouvelle entrée au journal matériel ces 7 derniers jours.</p>
        ) : (
          <ul className="sv-res-lignes">
            {sel.items.map((e) => {
              const msg = typeof e.payload['message_fr'] === 'string' ? e.payload['message_fr'] : e.kind_label
              const quand = typeof e.payload['created_corrected'] === 'string' ? e.payload['created_corrected'] : e.at
              return (
                <li key={e.id} className="sv-res-ligne">
                  <span className="sv-res-ligne__corps">
                    <span className="sv-res-ligne__meta sv-srv-sel">{msg}</span>
                    {details && typeof e.payload['message_id'] === 'string' ? <span className="sv-mono">{e.payload['message_id']}</span> : null}
                  </span>
                  <span className="sv-res-ligne__valeurs">
                    <Pastille ton={TON_GRAVITE[e.severity] ?? 'gris'}>{e.severity === 'critical' ? 'Critique' : e.severity === 'warning' ? 'Avertissement' : e.severity === 'error' ? 'Erreur' : 'Information'}</Pastille>
                    <span className="sv-res-age">{fmtDateHeure(quand)}</span>
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </Section>
  )
}

function fmtDuree(s: number): string {
  const a = Math.abs(s)
  if (a < 60) return fmtVal(a, 's', 1)
  if (a < 3600) return fmtVal(a / 60, 'min', 0)
  return fmtVal(a / 3600, 'h', 1)
}

/* ═══════════════ Conteneurs Docker ═══════════════ */

const RANG: Record<Ton, number> = { err: 0, warn: 1, gris: 2, maint: 3, off: 4, ok: 5 }

function SectionConteneurs({ doc, idx, perime, sources, details }: { doc: DocServeur | null; idx: Index; perime: boolean; sources: SourceDoc[]; details: boolean }) {
  const seuil = doc?.thresholds?.['thresholds.services.restarts_per_hour'] ?? null
  const liste = useMemo(() => (doc?.containers ?? [])
    .map((c) => ({ c, e: etatConteneur(c, seuil) }))
    .sort((a, b) => (RANG[a.e.ton] - RANG[b.e.ton]) || a.c.name.localeCompare(b.c.name, 'fr')), [doc, seuil])
  const aRegarder = liste.filter((x) => x.e.ton !== 'ok')
  const sains = liste.filter((x) => x.e.ton === 'ok')
  const collisions = valeur(idx, 'docker:routes', 'serveur.docker_vlan_collisions')
  const libres = valeur(idx, 'docker:pool', 'serveur.docker_pool_free')
  const flux = entites(idx, 'mediamtx_path:').map((e) => ({ nom: e.slice('mediamtx_path:'.length), s: valeur(idx, e, 'services.stream_not_ready_s') }))
  const fluxKo = flux.filter((f) => f.s !== null && f.s > 0)
  return (
    <Section id="sv-srv-conteneurs" titre="Conteneurs Docker" icone={<Container size={15} aria-hidden="true" />} perime={perime} droite={<Sources sources={sources} />}>
      <div className="sv-res-deux">
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete">
            <span className="sv-res-carte__titre">Garde réseau Docker / VLAN</span>
          </div>
          <ul className="sv-res-lignes">
            <li className="sv-res-ligne">
              <span className="sv-res-ligne__corps">
                <span className="sv-res-ligne__titre">Collision avec un réseau du club</span>
                <span className="sv-res-ligne__meta">Une route Docker qui couvre un VLAN du club couperait ses appareils du serveur.</span>
              </span>
              <Pastille ton={collisions === null ? 'gris' : collisions > 0 ? 'err' : 'ok'}>{collisions === null ? '—' : collisions > 0 ? `${collisions} collision${collisions > 1 ? 's' : ''}` : 'Aucune'}</Pastille>
            </li>
            <li className="sv-res-ligne">
              <span className="sv-res-ligne__corps">
                <span className="sv-res-ligne__titre">Réserve d’adresses Docker</span>
                <span className="sv-res-ligne__meta">Réseaux encore libres dans la plage 172.17–172.31.</span>
              </span>
              <Pastille ton={libres === null ? 'gris' : libres === 0 ? 'warn' : 'ok'}>{libres === null ? '—' : libres === 0 ? 'Épuisée' : `${libres} libre${libres > 1 ? 's' : ''}`}</Pastille>
            </li>
            {flux.length > 0 ? (
              <li className="sv-res-ligne">
                <span className="sv-res-ligne__corps">
                  <span className="sv-res-ligne__titre">Flux vidéo permanents (MediaMTX)</span>
                  <span className="sv-res-ligne__meta">{fluxKo.length === 0 ? `${flux.length} flux prêts.` : `Non prêts : ${fluxKo.map((f) => `${f.nom} (${fmtAge(f.s).replace('il y a ', 'depuis ')})`).join(', ')}.`}</span>
                </span>
                <Pastille ton={fluxKo.length > 0 ? 'warn' : 'ok'}>{flux.length - fluxKo.length}/{flux.length}</Pastille>
              </li>
            ) : null}
          </ul>
        </div>
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete">
            <span className="sv-res-carte__titre">À regarder</span>
            <span className="lc-hint">{aRegarder.length === 0 ? 'rien' : `${aRegarder.length} conteneur${aRegarder.length > 1 ? 's' : ''}`}</span>
          </div>
          {!doc ? <p className="lc-hint">—</p> : aRegarder.length === 0 ? <p className="lc-hint">Tous les conteneurs relevés tournent normalement.</p> : (
            <ul className="sv-res-lignes">
              {aRegarder.map(({ c, e }) => <LigneConteneur key={c.name} c={c} ton={e.ton} libelle={e.libelle} details={details} />)}
            </ul>
          )}
        </div>
      </div>
      {sains.length > 0 ? (
        <div className="sv-res-bloc">
          <h3 className="sv-res-sous-titre">En marche <span className="lc-hint">{sains.length} conteneur{sains.length > 1 ? 's' : ''}</span></h3>
          <ul className="sv-srv-conteneurs">
            {sains.map(({ c, e }) => (
              <li key={c.name} className="sv-srv-conteneur" title={`${e.libelle}${c.health_label ? ` · ${c.health_label}` : ''} · ${fmtNombre(c.restarts_1h)} redémarrage(s) en 1 h · relevé ${fmtAge(c.data_age_s)}`}>
                <span className="sv-srv-conteneur__point" aria-hidden="true" />
                {c.uid ? <LienAppareil uid={c.uid} nom={c.name} /> : <span>{c.name}</span>}
                {c.health === 2 ? <span className="sv-srv-conteneur__sain">sain</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Section>
  )
}

function LigneConteneur({ c, ton, libelle, details }: { c: Conteneur; ton: Ton; libelle: string; details: boolean }) {
  return (
    <li className="sv-res-ligne">
      <span className="sv-res-ligne__corps">
        {c.uid ? <LienAppareil uid={c.uid} nom={c.name} /> : <span className="sv-res-ligne__titre">{c.name}</span>}
        <span className="sv-res-ligne__meta">
          {c.running === true ? 'en marche' : c.running === false ? 'arrêté' : 'état —'}
          {c.health_label ? ` · ${c.health_label}` : ''} · {fmtNombre(c.restarts_1h)} redémarrage{(c.restarts_1h ?? 0) > 1 ? 's' : ''} en 1 h
          {c.oom_24h ? ` · ${c.oom_24h} saturation${c.oom_24h > 1 ? 's' : ''} mémoire en 24 h` : ''}
        </span>
        {details && c.uid ? <span className="sv-mono">{c.uid}</span> : null}
      </span>
      <span className="sv-res-ligne__valeurs"><Pastille ton={ton}>{libelle}</Pastille><Age s={c.data_age_s} at={c.at} /></span>
    </li>
  )
}

/* ═══════════════ Sauvegarde et certificats ═══════════════ */

function SectionSauvegarde({ idx, certificats: certs, perime, sources, seuilCert }: { idx: Index; certificats: Certificat[]; perime: boolean; sources: SourceDoc[]; seuilCert: { warn: number | null; crit: number | null } | null }) {
  const b = 'backup:serveur'
  const v = (m: string) => valeur(idx, b, m)
  const age = mesure(idx, b, 'serveur.backup_last_ok')?.data_age_s ?? null
  const dernierOk = oui(v('serveur.backup_last_ok'))
  const integre = oui(v('serveur.backup_integrity_ok'))
  const mensuelles = v('serveur.backup_monthly_count')
  const completes = v('serveur.backup_monthly_complete')
  const tonOui = (x: boolean | null): Ton => (x === null ? 'gris' : x ? 'ok' : 'err')
  const minJours = valeur(idx, 'npm', 'services.cert_min_days')
  return (
    <Section id="sv-srv-sauvegarde" titre="Sauvegarde et certificats" icone={<Archive size={15} aria-hidden="true" />} perime={perime} droite={<Sources sources={sources} />}>
      <div className="sv-res-deux">
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete">
            <span className="sv-res-carte__titre">Sauvegarde du serveur</span>
            <Age s={age} prefixe="relevé" />
          </div>
          {!idx.has(b) ? <Vide titre="Aucune sauvegarde relevée" texte="Sonde n’a pas encore lu l’état des sauvegardes du serveur." /> : (
            <>
              <ul className="sv-srv-puces">
                <li><span>Dernière sauvegarde</span><Pastille ton={tonOui(dernierOk)}>{dernierOk === null ? '—' : dernierOk ? 'Réussie' : 'En échec'}</Pastille></li>
                <li><span>Intégrité vérifiée</span><Pastille ton={tonOui(integre)}>{integre === null ? '—' : integre ? 'Oui' : 'Non'}</Pastille></li>
                <li><span>Problèmes signalés</span><Pastille ton={v('serveur.backup_failures') === null ? 'gris' : (v('serveur.backup_failures') ?? 0) > 0 ? 'warn' : 'ok'}>{fmtNombre(v('serveur.backup_failures'))}</Pastille></li>
                <li><span>Jours manquants</span><Pastille ton={v('serveur.backup_daily_gaps') === null ? 'gris' : (v('serveur.backup_daily_gaps') ?? 0) > 0 ? 'warn' : 'ok'}>{fmtNombre(v('serveur.backup_daily_gaps'))}</Pastille></li>
              </ul>
              <dl className="sv-res-faits">
                <Fait dt="Faite" dd={v('serveur.backup_age_h') === null ? '—' : fmtAge((v('serveur.backup_age_h') ?? 0) * 3600)} />
                <Fait dt="Taille" dd={fmtVal(v('serveur.backup_size_mb'), 'Mo', 0)} />
                <Fait dt="Copies quotidiennes" dd={fmtVal(v('serveur.backup_daily_count'), 'jours')} />
                <Fait dt="Copies mensuelles" dd={mensuelles === null ? '—' : `${fmtNombre(mensuelles)} (${fmtNombre(completes)} complète${(completes ?? 0) > 1 ? 's' : ''})`} />
                <Fait dt="Espace libre" dd={fmtVal(v('serveur.backup_free_gb'), 'Go', 0)} />
              </dl>
            </>
          )}
        </div>
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete">
            <span className="sv-res-carte__titre">Certificats des sites</span>
            <span className="lc-hint">alerte sous {fmtVal(seuilCert?.warn, 'j')} · critique sous {fmtVal(seuilCert?.crit, 'j')} · {fmtNombre(valeur(idx, 'npm', 'services.npm_hosts'))} hôtes publiés</span>
          </div>
          {certs.length === 0 ? <p className="lc-hint">Aucun certificat relevé.</p> : (
            <ul className="sv-srv-certs">
              {certs.map((c) => {
                const ton = tonMesure(c.niveau)
                const pct = c.jours === null ? null : Math.min(100, (c.jours / 90) * 100)
                return (
                  <li key={c.hote} className="sv-srv-cert">
                    <span className="sv-mono sv-srv-cert__hote">{c.hote}</span>
                    <Jauge pct={pct} ton={ton} label={`Jours restants de ${c.hote}`} />
                    <span className={`sv-res-num sv-res-txt--${ton}`}>{fmtVal(c.jours, 'j')}</span>
                  </li>
                )
              })}
            </ul>
          )}
          {minJours !== null ? <p className="lc-hint">Prochaine expiration dans {fmtVal(minJours, 'jours')}.</p> : null}
        </div>
      </div>
    </Section>
  )
}

/* ═══════════════ Réseau de l'hôte et services ═══════════════ */

function SectionReseauHote({ doc, idx, perime, sources }: { doc: DocServeur | null; idx: Index; perime: boolean; sources: SourceDoc[] }) {
  const ifs = doc?.interfaces ?? []
  const surPrincipal = valeur(idx, 'iface:bond0', 'serveur.bond_on_primary')
  const sondes = doc?.probes ?? []
  return (
    <Section id="sv-srv-reseau" titre="Réseau du serveur et services" icone={<Network size={15} aria-hidden="true" />} perime={perime} droite={<Sources sources={sources} />}>
      <div className="sv-res-deux">
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete">
            <span className="sv-res-carte__titre">Interfaces</span>
            {surPrincipal !== null ? <Pastille ton={surPrincipal === 1 ? 'ok' : 'warn'}>{surPrincipal === 1 ? 'Agrégat sur son lien principal' : 'Agrégat sur le lien de secours'}</Pastille> : null}
          </div>
          {ifs.length === 0 ? <p className="lc-hint">—</p> : (
            <div className="sv-res-table">
              <table className="lc-table">
                <thead><tr><th>Interface</th><th>Lien</th><th>Vitesse</th><th>Reçu / envoyé</th><th>Erreurs 1 h</th><th>Rejets 1 h</th><th>Coupures 24 h</th></tr></thead>
                <tbody>
                  {ifs.map((i) => (
                    <tr key={i.iface}>
                      <td className="sv-mono">{i.iface}{i.bond_active ? ' ★' : ''}</td>
                      <td><Pastille ton={estPerime(i.data_age_s) ? 'gris' : i.up === true ? 'ok' : i.up === false ? 'off' : 'gris'}>{estPerime(i.data_age_s) ? 'Périmé' : i.up === true ? 'Actif' : i.up === false ? 'Inactif' : '—'}</Pastille></td>
                      <td>{fmtVitesse(i.speed_mbps)}</td>
                      <td className="sv-res-num">{fmtDebit(i.rx_bps)} / {fmtDebit(i.tx_bps)}</td>
                      <td className="sv-res-num">{fmtNombre(i.rx_err_1h === null && i.tx_err_1h === null ? null : (i.rx_err_1h ?? 0) + (i.tx_err_1h ?? 0))}</td>
                      <td className="sv-res-num">{fmtNombre(i.rx_drop_1h === null && i.tx_drop_1h === null ? null : (i.rx_drop_1h ?? 0) + (i.tx_drop_1h ?? 0))}</td>
                      <td className="sv-res-num">{fmtNombre(i.carrier_changes_24h)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="lc-hint">★ lien actif de l’agrégat. Une interface inactive n’est pas une panne en soi (pont sans machine virtuelle, port de secours).</p>
        </div>
        <div className="sv-res-carte">
          <div className="sv-res-carte__tete"><span className="sv-res-carte__titre"><Globe size={14} aria-hidden="true" /> Adresses des services</span></div>
          {sondes.length === 0 ? <p className="lc-hint">—</p> : (
            <ul className="sv-res-lignes">
              {sondes.map((p) => {
                const ton = estPerime(p.data_age_s) ? 'gris' : tonMesure(p.level)
                return (
                  <li key={p.probe_key} className="sv-res-ligne">
                    <span className="sv-res-ligne__corps">
                      <span className="sv-res-ligne__titre">{libelleService(p.probe_key)}</span>
                      {p.http_status !== null ? <span className="sv-res-ligne__meta">HTTP {p.http_status}{p.err_class ? ` · ${p.err_class}` : ''}</span> : null}
                    </span>
                    <span className="sv-res-ligne__valeurs">
                      <Pastille ton={ton}>{p.ok === false ? 'Sans réponse' : fmtVal(p.latency_ms, 'ms')}</Pastille>
                      <Age s={p.data_age_s} at={p.at} />
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </Section>
  )
}

function libelleService(cle: string): string {
  if (cle === 'npm:proxy') return 'Nginx Proxy Manager'
  const n = cle.replace(/^endpoint:/, '').replace(/^npm:tls:/, 'Certificat ')
  const interne = n.endsWith('-interne')
  const base = (interne ? n.slice(0, -'-interne'.length) : n).replace(/-/g, ' ')
  return `${base.charAt(0).toUpperCase()}${base.slice(1)}${interne ? ' (accès interne)' : ''}`
}

/* ═══════════════ Fragilité ═══════════════ */

function SectionFragilite({ doc, perime, lecture }: { doc: DocFragilite | null; perime: boolean; lecture: string | null }) {
  const comps = [...(doc?.components ?? [])].sort((a, b) => (b.score ?? -1) - (a.score ?? -1))
  return (
    <Section id="sv-srv-fragilite" titre="Fragilité du club" icone={<Gauge size={15} aria-hidden="true" />} perime={perime}
      droite={doc ? <Age s={doc.age_s} at={doc.computed_at} prefixe="calculée" /> : null}>
      {!doc ? <p className="lc-hint">{lecture ?? '—'}</p> : (
        <>
          <div className="sv-srv-score">
            <span className={`sv-srv-score__valeur sv-res-txt--${tonFragilite(doc.level)}`}>{fmtNombre(doc.score)}<small>/100</small></span>
            <p className="sv-res-chapo">{doc.headline_fr}</p>
          </div>
          <ul className="sv-srv-composantes">
            {comps.map((c) => (
              <li key={c.key} className="sv-srv-composante">
                <span className="sv-srv-composante__tete">
                  <span className="sv-res-ligne__titre">{c.label}</span>
                  <span className={`sv-res-num sv-res-txt--${tonFragilite(c.level)}`}>{c.score === null ? '—' : `${c.score}/100`}</span>
                </span>
                <Jauge pct={c.score} ton={tonFragilite(c.level)} label={`Fragilité : ${c.label}`} />
                {c.score === null ? <span className="lc-hint">Pas encore de mesure pour cette partie.</span> : null}
                {c.factors.filter((f) => conseilAutorise(f.reason_fr)).map((f, i) => <span key={i} className="sv-res-ligne__meta">+{f.points} · {f.reason_fr}</span>)}
                {c.trend_fr && conseilAutorise(c.trend_fr) ? <span className="lc-hint">{c.trend_fr}</span> : null}
              </li>
            ))}
          </ul>
          {doc.devices.length > 0 ? (
            <div className="sv-res-bloc">
              <h3 className="sv-res-sous-titre">Appareils qui décrochent <span className="lc-hint">coupures sur 7 jours, bascules sur 24 h</span></h3>
              <ul className="sv-res-lignes">
                {doc.devices.map((d) => (
                  <li key={d.uid} className="sv-res-ligne">
                    <span className="sv-res-ligne__corps">
                      <LienAppareil uid={d.uid} nom={d.name} />
                      <span className="sv-res-ligne__meta">{d.reasons_fr.join(' · ')}</span>
                    </span>
                    <span className="sv-res-ligne__valeurs"><span className="sv-res-chiffre">{d.score}/100</span></span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </Section>
  )
}

/* ═══════════════ Prévisions ═══════════════ */

function SectionPrevisions({ doc, perime }: { doc: DocPrevisions | null; perime: boolean }) {
  const groupes = useMemo(() => {
    const m = new Map<string, Prevision[]>()
    for (const s of doc?.series ?? []) m.set(s.key, [...(m.get(s.key) ?? []), s])
    const rang = { crit: 0, warn: 1, unknown: 2, ok: 3 } as const
    return [...m.entries()].map(([k, l]) => ({
      cle: k,
      titre: GROUPE_PREVISION[k] ?? k,
      series: [...l].sort((a, b) => (rang[a.state] - rang[b.state]) || (a.eta_crit ?? '9').localeCompare(b.eta_crit ?? '9') || a.label.localeCompare(b.label, 'fr')),
    }))
  }, [doc])
  return (
    <Section id="sv-srv-previsions" titre="Prévisions" icone={<TrendingUp size={15} aria-hidden="true" />} perime={perime}
      droite={doc ? <span className="sv-res-sources"><span className="lc-hint">horizon {doc.horizon_days} jours</span><Age s={doc.age_s} at={doc.computed_at} prefixe="calculées" /></span> : null}>
      {!doc ? <p className="lc-hint">—</p> : !doc.enabled ? (
        <p className="lc-hint">Les prévisions sont coupées dans les réglages de Sonde.</p>
      ) : groupes.length === 0 ? (
        <p className="lc-hint">Pas encore de série assez longue pour prévoir.</p>
      ) : (
        <div className="sv-res-grille">
          {groupes.map((g) => (
            <div key={g.cle} className="sv-res-carte">
              <div className="sv-res-carte__tete"><span className="sv-res-carte__titre">{g.titre}</span><span className="lc-hint">{g.series.length} suivi{g.series.length > 1 ? 's' : ''}</span></div>
              <ul className="sv-srv-previsions">
                {g.series.map((s) => {
                  const ech = echeance(s)
                  return (
                    <li key={`${s.key}-${s.entity}`} className="sv-srv-prevision" title={s.message_fr}>
                      <span className="sv-srv-prevision__tete">
                        <span className="sv-res-ligne__titre">{s.label.replace(/^Certificat /, '').replace(/^Remplissage du disque /, 'Disque ')}</span>
                        <Pastille ton={tonPrevision(s.state)}>{fmtVal(s.current, s.unit, s.unit === '%' ? 1 : 0)}</Pastille>
                      </span>
                      <span className="sv-res-ligne__meta">
                        {ech ?? (s.confidence === 'insuffisante' ? `tendance : pas assez d’historique (${s.days} j)` : s.slope_per_day !== null ? `${s.slope_per_day > 0 ? '+' : ''}${fmtVal(s.slope_per_day, `${s.unit}/jour`, 2)} · rien avant ${doc.horizon_days} j` : 'rien avant l’horizon')}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Section>
  )
}
